/** tm/components/AIStepConfigPanel.tsx — AI 스텝 설정 패널 (프리셋 설정 스타일) */
import { useState, useRef } from 'react';
import type { AIStepConfig, AIPreset } from '../types';
import { C, T, R } from './ui';

interface Props {
  value: AIStepConfig;
  onChange: (v: AIStepConfig) => void;
  presets: AIPreset[];
  aiType: 'ai_questionnaire' | 'ai_bizplan';
  collectProjectId?: string;
  onViewCollect?: () => void;
}

const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 700, color: '#7C3AED', display: 'block', marginBottom: 6 };
const inp: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const };

export default function AIStepConfigPanel({ value, onChange, presets, aiType, collectProjectId, onViewCollect }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetLabel = useRef('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');

  const filtered = presets.filter(p => p.type === aiType);
  const files = value.files || [];

  function handlePreset(id: string) {
    const preset = filtered.find(p => p.id === id);
    if (!preset) { onChange({ ...value, presetId: '', files: [], extraPrompt: value.extraPrompt }); return; }
    const raw = preset as unknown as Record<string, unknown>;
    const labels = Array.isArray(raw.doc_labels) ? raw.doc_labels as string[] : [];
    const pFiles = Array.isArray(raw.files) ? raw.files as { label: string; name: string; s3_key: string }[] : [];
    const nextFiles = labels.map(lb => {
      const match = pFiles.find(f => f.label === lb);
      return match ? { label: lb, name: match.name, s3_key: match.s3_key } : { label: lb, name: '', s3_key: '' };
    });
    onChange({ ...value, presetId: id, files: nextFiles, extraPrompt: preset.prompt || value.extraPrompt });
  }

  function triggerUpload(label: string) {
    uploadTargetLabel.current = label;
    fileInputRef.current?.click();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const targetLabel = uploadTargetLabel.current;
    const token = localStorage.getItem('token') || '';
    const fd = new FormData();
    fd.append('file', f);
    fetch('/api/tm/upload-file', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd })
      .then(r => r.json())
      .then(json => {
        if (json.data) {
          const newFile = { label: targetLabel, name: json.data.name || f.name, s3_key: json.data.s3_key };
          const idx = files.findIndex(ff => ff.label === targetLabel);
          if (idx >= 0) {
            const next = [...files]; next[idx] = newFile;
            onChange({ ...value, files: next });
          } else {
            onChange({ ...value, files: [...files, newFile] });
          }
        }
      })
      .catch(() => {});
    e.target.value = '';
  }

  function removeFile(label: string) {
    onChange({ ...value, files: files.filter(f => f.label !== label) });
  }

  function addDocLabel(label: string) {
    if (!label.trim()) return;
    // 같은 이름 없으면 빈 파일 슬롯 추가
    if (!files.find(f => f.label === label.trim())) {
      onChange({ ...value, files: [...files, { label: label.trim(), name: '', s3_key: '' }] });
    }
    setNewDocLabel('');
    setShowAddLabel(false);
  }

  // 파일 목록에서 라벨만 추출 (빈 파일 포함)
  const fileLabels = files.map(f => f.label);

  return (
    <div>
      {/* AI 프리셋 선택 */}
      <div style={lbl}>AI 프리셋 (선택사항)</div>
      <select value={value.presetId || ''} onChange={e => handlePreset(e.target.value)}
        style={{ ...inp, marginBottom: 12 }}>
        <option value="">프리셋 없이 직접 설정</option>
        {filtered.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {/* AI에게 전달할 파일 */}
      <div style={lbl}>📎 AI에게 전달할 파일</div>

      {/* 1번: 앞단계 자료수집 파일 자동 전달 토글 */}
      <div onClick={() => onChange({ ...value, includeCollectAnswers: !value.includeCollectAnswers })}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, height: 48, padding: '0 12px',
          borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', marginBottom: 0, cursor: 'pointer',
          borderBottom: 'none', borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
        }}>
        <span style={{ fontSize: T.md, flexShrink: 0 }}>🔄</span>
        <span style={{ flex: 1, fontSize: T.sm, color: C.text, fontWeight: 500 }}>앞단계 자료수집 파일 자동 전달</span>
        <div style={{
          width: 38, height: 20, borderRadius: 10, position: 'relative', transition: 'background 0.2s', flexShrink: 0,
          background: value.includeCollectAnswers ? '#059669' : '#D1D5DB',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
            left: value.includeCollectAnswers ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </div>
      </div>
      <div style={{ height: 1, background: C.border, marginBottom: 3 }} />

      {/* 항목별 파일 업로드 행 */}
      {fileLabels.map(docLabel => {
        const uploaded = files.find(f => f.label === docLabel && f.s3_key);
        return (
          <div key={docLabel} style={{
            display: 'flex', alignItems: 'center', gap: 8, height: 48, padding: '0 12px',
            borderRadius: 6, border: `1px solid ${uploaded ? '#7C3AED' : C.border}`,
            background: uploaded ? '#F5F3FF' : '#fff', marginBottom: 3,
          }}>
            <span style={{ fontSize: T.md, flexShrink: 0 }}>{uploaded ? '✅' : '📌'}</span>
            <span style={{
              flex: 1, fontSize: T.sm, fontWeight: uploaded ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: uploaded ? '#7C3AED' : C.text,
            }}>
              {uploaded ? `${docLabel} — ${uploaded.name}` : docLabel}
            </span>
            {uploaded ? (
              <button onClick={() => removeFile(docLabel)}
                style={{ fontSize: T.xs, padding: '4px 8px', borderRadius: R.sm, border: '1px solid #DC262633', background: 'transparent', color: '#DC2626', cursor: 'pointer', flexShrink: 0 }}>✕</button>
            ) : (
              <button onClick={() => triggerUpload(docLabel)}
                style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: '1px solid #7C3AED', background: '#F5F3FF', color: '#7C3AED', cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}>
                📎 파일 선택
              </button>
            )}
            <button onClick={() => { removeFile(docLabel); }}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: T.xs, padding: '2px 4px', flexShrink: 0 }} title="항목 제거">✕</button>
          </div>
        );
      })}

      {/* 항목 추가 */}
      {showAddLabel ? (
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          <input value={newDocLabel} onChange={e => setNewDocLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newDocLabel.trim()) addDocLabel(newDocLabel); }}
            placeholder="항목명 입력 (예: 사업계획서 양식)" autoFocus
            style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: '1px solid #7C3AED', fontSize: T.sm, outline: 'none' }} />
          <button onClick={() => addDocLabel(newDocLabel)} disabled={!newDocLabel.trim()}
            style={{ padding: '6px 12px', borderRadius: R.sm, border: 'none', background: newDocLabel.trim() ? '#7C3AED' : '#ddd', color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: newDocLabel.trim() ? 'pointer' : 'default' }}>추가</button>
          <button onClick={() => { setShowAddLabel(false); setNewDocLabel(''); }}
            style={{ padding: '6px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
        </div>
      ) : (
        <button onClick={() => setShowAddLabel(true)}
          style={{ width: '100%', padding: '8px 0', marginTop: 4, borderRadius: R.sm, border: '1.5px dashed #7C3AED44', background: 'transparent', color: '#7C3AED', fontSize: T.xs, fontWeight: 600, cursor: 'pointer', marginBottom: 12 }}>
          + 항목 추가
        </button>
      )}

      {/* AI 지시사항 (프롬프트) */}
      <div style={lbl}>🤖 AI 지시사항 (프롬프트)</div>
      <textarea value={value.extraPrompt || ''} onChange={e => onChange({ ...value, extraPrompt: e.target.value })}
        placeholder="AI에게 전달할 추가 지시사항을 입력하세요..."
        rows={4} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} />

      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileChange} />
    </div>
  );
}
